import DiffMatchPatch from "diff-match-patch";

// Side-by-side diff for the history view: each edited line sits next to its new version, even
// after a heavy rewrite, with the words that changed marked on both sides.

/** A stretch of one side of a line, and whether it changed. */
export type Seg = [changed: boolean, text: string];
/** One row of the diff. A side is missing where a line was only added or only removed. */
export type Row = { same: true; text: string } | { same: false; old?: Seg[]; neu?: Seg[] };

type Op = [op: number, text: string];

const dmp = new DiffMatchPatch();
const solid = (s: string) => s.replace(/\s/g, "").length;

function bigrams(s: string) {
  const m = new Map<string, number>();
  const t = s.replace(/\s+/g, " ").trim();
  for (let i = 0; i < t.length - 1; i++) m.set(t.slice(i, i + 2), (m.get(t.slice(i, i + 2)) ?? 0) + 1);
  return m;
}

function sharedBigrams(x: Map<string, number>, y: Map<string, number>) {
  const [small, big] = x.size < y.size ? [x, y] : [y, x];
  let n = 0;
  for (const [g, c] of small) n += Math.min(c, big.get(g) ?? 0);
  return n;
}

const TOKEN = /\\\p{L}+|[\p{L}\p{N}_]+|\s+|[^\p{L}\p{N}_\s]/gu;

// Diff of two lines word by word (spaces and each punctuation mark count as words), or character by
// character when there are too many distinct words to encode one per character.
function tokenDiff(a: string, b: string): Op[] {
  const ids = new Map<string, number>();
  const words: string[] = [];
  const enc = (s: string) => {
    let out = "";
    for (const t of s.match(TOKEN) ?? []) {
      if (!ids.has(t)) {
        ids.set(t, words.length);
        words.push(t);
      }
      out += String.fromCharCode(ids.get(t)!);
    }
    return out;
  };
  const ea = enc(a);
  const eb = enc(b);
  if (words.length > 0xd000) return dmp.diff_main(a, b);
  return dmp.diff_main(ea, eb, false).map(([op, s]) => [op, s.split("").map((c) => words[c.charCodeAt(0)]).join("")]);
}

// Narrows a word swapped for a near copy of itself ("Apple" → "apple", "colour" → "color") to the
// letters that changed, but not one that would come out choppy ("red" → "green", "sky" → "sea") or
// that sits in a longer change ("as hole" → "conditions. Hole").
function narrow(d: Op[]): Op[] {
  const out: Op[] = [];
  const apart = (i: number, beyond: number) => !d[beyond] || ANCHOR.test(d[i][1]);
  for (let k = 0; k < d.length; k++) {
    const next = d[k + 1];
    if (d[k][0] === -1 && next?.[0] === 1 && !/\s/.test(d[k][1] + next[1]) && (k === 0 || apart(k - 1, k - 2)) && (!d[k + 2] || apart(k + 2, k + 3))) {
      const c = dmp.diff_main(d[k][1], next[1]);
      const stretches = c.filter(([op], i) => op !== 0 && (i === 0 || c[i - 1][0] === 0)).length;
      const common = c.reduce((n, [op, t]) => n + (op === 0 ? t.length : 0), 0);
      if (stretches === 1 && common >= Math.max(d[k][1].length, next[1].length) / 2) {
        out.push(...c);
        k++;
        continue;
      }
    }
    out.push(d[k]);
  }
  return out;
}

// Text that still says something when it survives a rewrite: a word of four or more letters or a
// LaTeX command. "the", "of a", "}{" or a lone number left between two changes is coincidence.
const ANCHOR = /\p{L}{4,}|\\\p{L}/u;

// One side of a diff (`op` −1 for the old line, 1 for the new): what it kept and what it changed.
// Filler kept between two changes joins them, so the changes read as phrases.
function side(d: Op[], op: number): Seg[] {
  const out: Seg[] = [];
  for (const [o, t] of d) {
    if (o !== 0 && o !== op) continue;
    const last = out[out.length - 1];
    if (last?.[0] === (o !== 0)) last[1] += t;
    else out.push([o !== 0, t]);
  }
  for (let k = out.length - 2; k > 0; k--)
    if (!out[k][0] && out[k - 1][0] && out[k + 1][0] && !ANCHOR.test(out[k][1])) {
      out[k - 1][1] += out[k][1] + out[k + 1][1];
      out.splice(k, 2);
    }
  return out;
}

const kept = (s: Seg[]) => s.reduce((n, [changed, t]) => n + (changed ? 0 : solid(t)), 0);

// The old and new line with their changes marked, or null when they have too little in common to
// be versions of the same line.
function lineChanges(a: string, b: string): [Seg[], Seg[]] | null {
  const d = narrow(tokenDiff(a, b));
  const old = side(d, -1);
  const neu = side(d, 1);
  return Math.min(kept(old), kept(neu)) >= 0.25 * Math.max(solid(a), solid(b)) ? [old, neu] : null;
}

type Pair = { i: number; j: number; sides: [Seg[], Seg[]] | null };

// Within a block of replaced lines, matches each removed line to the added line it most resembles,
// keeping order. Identical lines (blank ones between edited paragraphs) pair with no sides. In a big
// block only lines near the same place are compared: that much change is mostly in place (a section
// commented out, a command renamed throughout).
function pairLines(dels: string[], adds: string[]): Pair[] {
  const m = dels.length;
  const n = adds.length;
  if (!m || !n || m * n > 4_000_000) return [];
  const reach = m * n <= 250_000 ? n : Math.max(20, Math.floor(125_000 / m));
  const gd = dels.map(bigrams);
  const ga = adds.map(bigrams);
  // Tables are flat, row i (1-based, a removed line) by column j (an added line).
  const W = n + 1;
  const w = new Int32Array((m + 1) * W);
  for (let i = 1; i <= m; i++) {
    const at = (i * n) / m;
    for (let j = Math.max(1, Math.ceil(at - reach)); j <= Math.min(n, Math.floor(at + reach)); j++) {
      const s = sharedBigrams(gd[i - 1], ga[j - 1]);
      if (dels[i - 1] === adds[j - 1]) w[i * W + j] = s + 1;
      else if (s > 0 && s >= 0.5 * Math.min(gd[i - 1].size, ga[j - 1].size)) w[i * W + j] = s;
    }
  }
  // Best order-preserving matching by total similarity.
  const dp = new Int32Array((m + 1) * W);
  for (let i = 1; i <= m; i++)
    for (let j = 1, k = i * W + 1; j <= n; j++, k++) dp[k] = Math.max(dp[k - W], dp[k - 1], w[k] ? dp[k - W - 1] + w[k] : 0);
  const pairs: Pair[] = [];
  for (let i = m, j = n; i > 0 && j > 0; ) {
    const k = i * W + j;
    if (dp[k] === dp[k - W]) i--;
    else if (dp[k] === dp[k - 1]) j--;
    else {
      if (dels[i - 1] === adds[j - 1]) pairs.push({ i: i - 1, j: j - 1, sides: null });
      else {
        const sides = lineChanges(dels[i - 1], adds[j - 1]);
        if (sides) pairs.push({ i: i - 1, j: j - 1, sides });
      }
      i--;
      j--;
    }
  }
  return pairs.reverse();
}

export function computeDiff(a: string, b: string): Row[] {
  const { chars1, chars2, lineArray } = dmp.diff_linesToChars_(a, b);
  const diffs = dmp.diff_main(chars1, chars2, false);
  dmp.diff_charsToLines_(diffs, lineArray);
  const rows: Row[] = [];
  let dels: string[] = [];
  let adds: string[] = [];
  const flush = () => {
    let i = 0;
    let j = 0;
    // Lines with no partner go side by side with whatever else changed there.
    const loose = (ti: number, tj: number) => {
      while (i < ti || j < tj)
        rows.push({ same: false, old: i < ti ? [[false, dels[i++]]] : undefined, neu: j < tj ? [[false, adds[j++]]] : undefined });
    };
    for (const p of pairLines(dels, adds)) {
      loose(p.i, p.j);
      rows.push(p.sides ? { same: false, old: p.sides[0], neu: p.sides[1] } : { same: true, text: adds[j] });
      i++;
      j++;
    }
    loose(dels.length, adds.length);
    dels = [];
    adds = [];
  };
  diffs.forEach(([op, text], k) => {
    const lines = text.split("\n");
    if (lines[lines.length - 1] === "") lines.pop();
    if (op !== 0) for (const line of lines) (op === -1 ? dels : adds).push(line);
    // Blank lines (or a lone brace) between two edits are weak anchors: the line diff can match one
    // to the wrong blank and split an edited paragraph from its original, so let pairing re-align them.
    else if ((dels.length || adds.length) && k < diffs.length - 1 && lines.every((l) => solid(l) <= 1))
      for (const line of lines) {
        dels.push(line);
        adds.push(line);
      }
    else {
      flush();
      for (const line of lines) rows.push({ same: true, text: line });
    }
  });
  flush();
  return rows;
}
